import React, { memo, useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SectionList } from '@Sage/ssm-web5';
import classnames from 'classnames';
import { Button, Icon } from '@Sage/hoi-poi-ui';
import { normalizeProductUnitRelations } from '@Sage/ssm-utils';

import { EntityDetailActions, EntityListActions } from 'actions';
import { PRODUCTS } from 'constants/Entities';
import Context from 'managers/Context';
import { getProductUoMRelations, deleteProductUoMRelation } from 'services/ProductsService';
import { publish } from 'lib/EventBuser';
import { UPDATE_ROW } from 'lib/events';
import { getBackendBoolean } from '@Sage/ssm-utils/backendScalars';
import { formatDecimalByConfig, DECIMAL_TYPES } from 'utils/decimal';
import { getLiteral } from 'utils/getLiteral';
import { errorToast } from 'utils/toast';

import DeleteModal from 'components/DeleteModal';

import ProductUnitRelationsModal from './ProductUnitRelationsModal';
import ProductUnitRelationsEmpty from './ProductUnitRelationsEmpty';
import ProductUnitRelationNameCell from './ProductUnitRelationNameCell';

import './styles.scss';

/** Grid display for factor / minStep: no fixed decimal padding; strings from API stay as trimmed text. */
const formatUomNumericListDisplay = (value) => {
    if (value === null || value === undefined || value === '') return '';
    if (typeof value === 'string') {
        const t = value.trim();
        return t === '' ? '' : t;
    }
    if (typeof value === 'number') {
        if (!Number.isFinite(value)) return '';
        return String(value);
    }
    return String(value);
};

const ProductUnitRelations = memo(({ id, idCurrency, idEnvironment, relations }) => {
    const dispatch = useDispatch();
    const productCrud = useSelector(
        (state) => state.config.permission.crud_permission[PRODUCTS.permission],
    );
    const modalRef = useRef(null);
    const deleteRef = useRef(null);
    const [loading, setLoading] = useState(relations === undefined);
    const [error, setError] = useState(false);
    const data = useMemo(() => normalizeProductUnitRelations(relations), [relations]);

    const load = useCallback(() => {
        if (!id) {
            setLoading(false);
            return Promise.resolve([]);
        }
        setLoading(true);
        return getProductUoMRelations({ idProduct: id })
            .then((result) => {
                const nextRelations = normalizeProductUnitRelations(result);
                dispatch(
                    EntityDetailActions.updateDetailFields(PRODUCTS, id, {
                        productUnitRelations: nextRelations,
                    }),
                );
                const rows = Context.store.getState()?.entityList?.[PRODUCTS.entity]?.data;
                if (Array.isArray(rows)) {
                    const row = rows.find(
                        (item) => item?.product && String(item.product.Id) === String(id),
                    );
                    if (row?.product) {
                        const nextProduct = {
                            ...row.product,
                            productUnitRelations: nextRelations,
                        };
                        dispatch(
                            EntityListActions.updateRowFields(PRODUCTS, row.id, {
                                product: nextProduct,
                            }),
                        );
                        // Lazy product list uses ag-grid row cache (rows prop is null); patch the visible row.
                        publish(`${UPDATE_ROW}_${PRODUCTS.entity}`, {
                            id: String(row.id),
                            fields: { product: nextProduct },
                        });
                    }
                }
                setError(false);
                return nextRelations;
            })
            .catch((e) => {
                console.error(e);
                setError(true);
                throw e;
            })
            .finally(() => setLoading(false));
    }, [dispatch, id]);

    const onDelete = useCallback(
        (relationId) =>
            deleteProductUoMRelation({ idProduct: id, idRelation: relationId })
                .then(load)
                .catch((e) => {
                    console.error(e);
                    errorToast({
                        text: getLiteral('error_uom_delete_base_unit_with_active_units'),
                    });
                }),
        [id, load],
    );

    const showDeleteModal = useCallback((relationId) => {
        deleteRef.current?.open({ id: relationId });
    }, []);

    const openAddModal = useCallback(() => {
        const maxOrder =
            data?.length > 0 ? Math.max(0, ...data.map((r) => Number(r.intOrder) || 0)) : 0;
        modalRef.current?.open({ idProduct: id, intOrder: String(maxOrder + 1) });
    }, [id, data]);

    const openEditModal = useCallback(
        (relation) => {
            modalRef.current?.open({ ...relation, idProduct: id });
        },
        [id],
    );

    const getRowNodeId = useCallback((row) => row.id, []);

    const sectionListComponents = useMemo(
        () => ({
            ProductUnitRelationNameCell,
        }),
        [],
    );

    const columns = useMemo(() => {
        return [
            {
                colId: 'name',
                field: 'name',
                headerName: getLiteral('label_unit_name'),
                headerComponent: 'headerTextCell',
                headerComponentParams: { showTotal: true },
                cellRenderer: 'ProductUnitRelationNameCell',
                cellRendererParams: {
                    baseBadgeLabel: getLiteral('label_unit_type_base'),
                },
                minWidth: 260,
                resizable: true,
            },
            {
                colId: 'unitOfMeasure',
                field: 'unitOfMeasure.descripcion',
                headerName: getLiteral('label_unit_of_measure'),
                cellRenderer: 'textCell',
                minWidth: 160,
                resizable: true,
                valueGetter: ({ data: row }) =>
                    row?.unitOfMeasure?.descripcion || row?.unitOfMeasure?.description || '',
            },
            {
                colId: 'factor',
                field: 'factor',
                headerName: getLiteral('label_factor'),
                cellRenderer: 'textCell',
                minWidth: 100,
                resizable: true,
                valueGetter: ({ data: row }) => formatUomNumericListDisplay(row?.factor),
            },
            {
                colId: 'minStep',
                field: 'minStep',
                headerName: getLiteral('label_precision'),
                cellRenderer: 'textCell',
                minWidth: 100,
                resizable: true,
                valueGetter: ({ data: row }) => formatUomNumericListDisplay(row?.minStep),
            },
            {
                colId: 'specialPrice',
                field: 'specialPrice',
                headerName: getLiteral('label_special_price'),
                cellRenderer: 'textCell',
                minWidth: 120,
                resizable: true,
                valueGetter: ({ data: row }) =>
                    getBackendBoolean(row?.useSpecialPrice) &&
                    row?.specialPrice !== null &&
                    row?.specialPrice !== undefined &&
                    row?.specialPrice !== ''
                        ? formatDecimalByConfig({
                              value: row.specialPrice,
                              type: DECIMAL_TYPES.CURRENCY_PRICES,
                              idEnvironment,
                              idCurrency: idCurrency !== '-1' ? idCurrency : undefined,
                              entityName: 'PRODUCTS',
                              fieldId: 'precio',
                          })
                        : '',
            },
        ];
    }, [idCurrency, idEnvironment]);

    const actions = useMemo(() => {
        const list = [];
        if (productCrud?.update) {
            list.push((row) => ({
                icon: <Icon name="edit" onClick={() => openEditModal(row)} />,
                tooltip: { content: getLiteral('action_edit') },
            }));
        }
        if (productCrud?.delete) {
            list.push((row) => ({
                icon: <Icon name="delete" onClick={() => showDeleteModal(row.id)} />,
                tooltip: { content: getLiteral('action_delete') },
            }));
        }
        return list;
    }, [openEditModal, productCrud?.delete, productCrud?.update, showDeleteModal]);

    const emptyViewProps = useMemo(
        () => ({ onClick: productCrud?.create ? openAddModal : undefined }),
        [openAddModal, productCrud?.create],
    );

    useEffect(() => {
        if (relations !== undefined || !id) {
            setLoading(false);
            setError(false);
            return;
        }
        load().catch(() => {});
    }, [id, load, relations]);

    const rootClass = classnames('fm-products-detail__product-unit-relations', {
        'fm-products-detail__product-unit-relations--empty':
            !loading && (error || data?.length === 0),
    });

    return (
        <div className={rootClass}>
            {productCrud?.create && (
                <div className="product-unit-relations-table__toolbar">
                    <Button
                        className="product-unit-relations-table__add-button"
                        type="secondary"
                        size="small"
                        onClick={openAddModal}
                    >
                        {getLiteral('action_add_unit')}
                    </Button>
                </div>
            )}
            <SectionList
                id="product-unit-relations-table"
                className="product-unit-relations-table"
                columnDefs={columns}
                rowData={data}
                getRowNodeId={getRowNodeId}
                emptyViewProps={emptyViewProps}
                customEmptyViewComponent={ProductUnitRelationsEmpty}
                useSort={false}
                isLoading={loading}
                actions={actions}
                components={sectionListComponents}
                forceActionsRefresh
                forceRowUpdating
            />
            <DeleteModal
                title={getLiteral('action_delete_unit_relation')}
                body={getLiteral('confirm_delete_unit_relation')}
                onRef={(ref) => {
                    deleteRef.current = ref;
                }}
                onDelete={onDelete}
            />
            <ProductUnitRelationsModal
                idCurrency={idCurrency}
                idEnvironment={idEnvironment}
                onRef={(ref) => {
                    modalRef.current = ref;
                }}
                onSaveSuccess={load}
            />
        </div>
    );
});

export default ProductUnitRelations;
